import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { User, GraduationCap, Award, Calendar, Download, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = process.env.REACT_APP_BACKEND_URL || '';

const StudentProfile = ({ user }) => {
  const [studentData, setStudentData] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const logoUrl = 'https://customer-assets.emergentagent.com/job_edumanage-196/artifacts/5gc25pr5_GeniusKid%20Logo.svg';

  useEffect(() => {
    fetchStudentData();
    fetchCertificates();
    fetchAttendanceHistory();
  }, []);

  const fetchStudentData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentData(response.data);
    } catch (error) {
      console.error('Error fetching student data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/attendance/certificates/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificates(response.data.certificates);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/attendance/history/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceHistory(response.data);
    } catch (error) {
      console.error('Error fetching attendance history:', error);
    }
  };

  const downloadCertificate = (certificate) => {
    const doc = new jsPDF();
    
    // Certificate border
    doc.setLineWidth(3);
    doc.setDrawColor(34, 197, 94); // Green border
    doc.rect(10, 10, 190, 277);
    
    doc.setLineWidth(1);
    doc.setDrawColor(200, 200, 200);
    doc.rect(15, 15, 180, 267);
    
    // Header
    doc.setFontSize(26);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 197, 94);
    doc.text('CERTIFICATE OF ACHIEVEMENT', 105, 40, { align: 'center' });
    
    // Subheading
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('100% Attendance Achiever', 105, 55, { align: 'center' });
    
    // Decorative line
    doc.setDrawColor(34, 197, 94);
    doc.setLineWidth(0.5);
    doc.line(40, 65, 170, 65);
    
    // Certificate body
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('This is to certify that', 105, 85, { align: 'center' });
    
    // Student name
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 197, 94);
    doc.text(certificate.student_name, 105, 105, { align: 'center' });
    
    // Achievement text
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('has achieved', 105, 125, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PERFECT ATTENDANCE', 105, 140, { align: 'center' });
    
    // Period details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    doc.text(`For the month of ${monthNames[certificate.month - 1]} ${certificate.year}`, 105, 155, { align: 'center' });
    
    // Stats box
    doc.setFillColor(240, 253, 244); // Light green
    doc.rect(40, 170, 130, 30, 'F');
    doc.setDrawColor(34, 197, 94);
    doc.rect(40, 170, 130, 30);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Classes: ${certificate.total_classes}`, 45, 182);
    doc.text(`Classes Attended: ${certificate.attended_classes}`, 45, 192);
    doc.setTextColor(34, 197, 94);
    doc.text(`Attendance: ${certificate.percentage}%`, 125, 187);
    
    // Center name
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text(certificate.center_name, 105, 220, { align: 'center' });
    
    // Signature line
    doc.setDrawColor(0, 0, 0);
    doc.line(130, 250, 180, 250);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Authorized Signature', 155, 256, { align: 'center' });
    
    // Date
    doc.text(`Issued on: ${new Date(certificate.generated_at).toLocaleDateString()}`, 105, 270, { align: 'center' });
    
    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('GeniusKid Education - Excellence in Learning', 105, 280, { align: 'center' });
    
    doc.save(`Certificate_${certificate.month}_${certificate.year}_${certificate.student_name}.pdf`);
    toast.success('Certificate downloaded!');
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card className="p-6">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
            {studentData?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {studentData?.name}
            </h2>
            <p className="text-gray-600">{studentData?.email}</p>
            <div className="flex gap-4 mt-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                {studentData?.gk_code || 'No GK Code'}
              </span>
              <span className={`px-3 py-1 text-sm rounded-full ${
                studentData?.status === 'active' ? 'bg-green-100 text-green-800' :
                studentData?.status === 'on_break' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {studentData?.status?.toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Last Month Achievement Badge */}
          {certificates.length > 0 && certificates[0].percentage === 100 && (
            <div className="text-center p-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg text-white">
              <Trophy className="w-8 h-8 mx-auto mb-2" />
              <p className="text-xs font-semibold">100% Attendance</p>
              <p className="text-xs">{getMonthName(certificates[0].month)} Achiever</p>
            </div>
          )}
        </div>
      </Card>

      {/* Profile Tabs */}
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="personal">
            <User className="w-4 h-4 mr-2" />
            Personal Info
          </TabsTrigger>
          <TabsTrigger value="academic">
            <GraduationCap className="w-4 h-4 mr-2" />
            Academic Info
          </TabsTrigger>
          <TabsTrigger value="certificates">
            <Award className="w-4 h-4 mr-2" />
            Certificates ({certificates.length})
          </TabsTrigger>
          <TabsTrigger value="attendance">
            <Calendar className="w-4 h-4 mr-2" />
            Attendance
          </TabsTrigger>
        </TabsList>

        {/* Personal Info Tab */}
        <TabsContent value="personal">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="font-semibold">{studentData?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{studentData?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date of Birth</p>
                <p className="font-semibold">
                  {studentData?.date_of_birth ? new Date(studentData.date_of_birth).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Age</p>
                <p className="font-semibold">{calculateAge(studentData?.date_of_birth)} years</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Gender</p>
                <p className="font-semibold">{studentData?.gender || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Contact Number</p>
                <p className="font-semibold">{studentData?.contact_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Alternate Contact</p>
                <p className="font-semibold">{studentData?.alternate_contact_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Father's Name</p>
                <p className="font-semibold">{studentData?.father_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Mother's Name</p>
                <p className="font-semibold">{studentData?.mother_name || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-semibold">{studentData?.full_address || 'N/A'}</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Academic Info Tab */}
        <TabsContent value="academic">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Academic Information</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600">GK Code</p>
                <p className="font-semibold">{studentData?.gk_code || 'Not Assigned'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date of Joining</p>
                <p className="font-semibold">
                  {studentData?.date_of_joining ? new Date(studentData.date_of_joining).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Module</p>
                <p className="font-semibold uppercase">{studentData?.module || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Group</p>
                <p className="font-semibold uppercase">{studentData?.group || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Student Mode</p>
                <p className="font-semibold uppercase">{studentData?.student_mode || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Term</p>
                <p className="font-semibold uppercase">{studentData?.payment_term || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">School Name</p>
                <p className="font-semibold">{studentData?.school_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Grade</p>
                <p className="font-semibold">{studentData?.grade || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`px-3 py-1 text-sm rounded-full ${
                  studentData?.status === 'active' ? 'bg-green-100 text-green-800' :
                  studentData?.status === 'on_break' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {studentData?.status?.toUpperCase()}
                </span>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates">
          <div className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">My Certificates</h3>
                <div className="text-sm text-gray-600">
                  Total: <span className="font-bold text-green-600">{certificates.length}</span> certificates
                </div>
              </div>

              {certificates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {certificates.map((cert, idx) => (
                    <Card key={idx} className="p-4 border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-green-700">100% Attendance Achiever</h4>
                            <p className="text-sm text-gray-600">{getMonthName(cert.month)} {cert.year}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Classes:</span>
                          <span className="font-semibold">{cert.total_classes}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Attended:</span>
                          <span className="font-semibold text-green-600">{cert.attended_classes}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Percentage:</span>
                          <span className="font-bold text-green-700">{cert.percentage}%</span>
                        </div>
                      </div>

                      <Button 
                        onClick={() => downloadCertificate(cert)}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Certificate
                      </Button>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No certificates yet</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Maintain 100% attendance to earn certificates!
                  </p>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Attendance History</h3>
            {attendanceHistory && attendanceHistory.total_records > 0 ? (
              <div>
                <div className="mb-4 text-sm text-gray-600">
                  Total Records: <span className="font-bold">{attendanceHistory.total_records}</span>
                </div>
                <div className="border rounded-lg overflow-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Batch</th>
                        <th className="px-4 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceHistory.records.map((record, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">{new Date(record.date).toLocaleDateString()}</td>
                          <td className="px-4 py-2">{record.batch_name}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              record.status === 'present' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {record.status.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No attendance records yet</p>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentProfile;
